// frontend/src/utils/policy/validateResponseAgainstPolicy.js
//
// This is dAIsy's response execution boundary. It validates a candidate reply
// against the effective typed policy and emits structured evidence describing
// every enforcement decision.
//
// Mechanical parsing may use punctuation to identify sentence boundaries.
// Punctuation does NOT decide conversational intent or policy. Runtime state
// already made that decision and supplied it through the policy contract.

import {
  RESPONSE_POLICY_ACTIONS,
  RESPONSE_POLICY_DECISIONS,
} from './responsePolicyContract';

function normalizeText(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function splitCandidateIntoSentences(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return [];

  const matches = normalized.match(/[^.!?]+[.!?]?/g);
  return (matches || []).map((sentence) => sentence.trim()).filter(Boolean);
}

function isInterrogative(sentence = '') {
  return /\?\s*$/.test(String(sentence || '').trim());
}

export function validateResponseAgainstPolicy(candidateResponse = '', policy = null) {
  const candidate = normalizeText(candidateResponse);
  const sentences = splitCandidateIntoSentences(candidate);
  const violations = [];
  const sentenceDecisions = [];

  if (!policy || !candidate) {
    return {
      decision: RESPONSE_POLICY_DECISIONS.ALLOW,
      candidateResponse: candidate,
      policyId: policy?.policyId || null,
      violations,
      sentenceDecisions,
    };
  }

  sentences.forEach((sentence, sentenceIndex) => {
    const optionalQuestionsForbidden =
      policy.rules?.allowOptionalQuestion === false &&
      policy.rules?.questionAuthority === 'none';

    if (optionalQuestionsForbidden && isInterrogative(sentence)) {
      const violation = {
        rule: 'allowOptionalQuestion',
        sentenceIndex,
        sentence,
        action: RESPONSE_POLICY_ACTIONS.REMOVE_SENTENCE,
        reason: 'Runtime policy grants no optional-question authority.',
      };

      violations.push(violation);
      sentenceDecisions.push(violation);
      return;
    }

    sentenceDecisions.push({
      sentenceIndex,
      sentence,
      action: RESPONSE_POLICY_ACTIONS.PRESERVE,
    });
  });

  const preservedCount = sentenceDecisions.filter(
    (item) => item.action === RESPONSE_POLICY_ACTIONS.PRESERVE
  ).length;

  let decision = RESPONSE_POLICY_DECISIONS.ALLOW;
  if (violations.length && preservedCount) {
    decision = RESPONSE_POLICY_DECISIONS.TRANSFORM;
  } else if (violations.length && !preservedCount) {
    decision = policy.fallbackResponse
      ? RESPONSE_POLICY_DECISIONS.REJECT
      : RESPONSE_POLICY_DECISIONS.REGENERATE;
  }

  return {
    decision,
    candidateResponse: candidate,
    policyId: policy.policyId,
    policyVersion: policy.version,
    policySource: policy.source,
    policyAuthority: policy.authority,
    violations,
    sentenceDecisions,
  };
}

export default validateResponseAgainstPolicy;
